import React, { useEffect, useRef, useState } from "react";

import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";

import { hideLoader, showLoader } from "../../../../store/actions";
import {
  getBankDetails,
  getBankDetailsFromIFSC,
  saveBankDetails,
  savePennyDetails,
} from "../../../../store/actions/newSubscriber";
import { useNewSubscriberContext } from "../Context/NewSubsciberContext";
import {
  setBankDetails as setBankDetailsContext,
  setBasicDetails,
  setPennyDropTypes,
  setStatusBackToSummary,
} from "../Context/NewSubscriberAction";
import { Checkbox } from "@material-ui/core";
import { Field, Form, Formik, useFormikContext, useField } from "formik";
import * as Yup from "yup";
import FormTextInput from "./lib/formik/InputField";
import FormRadioGroup from "./lib/formik/RadioGroup";
import CardComponent from "../../transactions/newInvestor/lib/CardComponent";
import CheckboxField from "./lib/formik/CheckboxField";
import { Dialog, DialogContent, DialogTitle } from "@material-ui/core";
import { SUCCESS_RESPONSE_CODE } from ".";

/**
 * Define a function that takes a given bank ifsc code and returns a promise that resolves to the bank name. Cache the results to prevent duplicate requests.
 */
const ifscMap = new Map();
async function fetchBankNameFromIFSC({ ifsc, dispatch }) {
  if (ifscMap.has(ifsc)) {
    return ifscMap.get(ifsc);
  }
  const promise = dispatch(getBankDetailsFromIFSC({ number: ifsc }));
  ifscMap.set(ifsc, promise);
  return promise;
}

function SimpleCheckboxFormikField({ name, ...rest }) {
  const [field] = useField(name);
  const { value, ...other } = field;
  const formikContext = useFormikContext();
  const { isSubmitting: disabled } = formikContext;
  return <Checkbox checked={value} disabled={disabled} {...other} {...rest} />;
}

const BankSection = ({ name, resident, openModal }) => {
  const dispatch = useDispatch();
  const isChanged = useRef(false);

  const { isBackToSummary } = useNewSubscriberContext();

  const { setFieldValue, setValues, values } = useFormikContext();

  return (
    <Field name={name}>
      {({ field: { name: fieldName } }) => {
        return (
          <div>
            <div className="new-investor-container">
              <FormTextInput
                label="IFSC Number"
                name={`${fieldName}.ifsc`}
                required
                onBlur={async (e) => {
                  const response = await fetchBankNameFromIFSC({
                    ifsc: e.target.value,
                    dispatch,
                  });
                  if (+response.statusCode === SUCCESS_RESPONSE_CODE) {
                    setFieldValue(
                      `${fieldName}.name`,
                      response.data[0].BankName
                    );
                  } else {
                    setFieldValue(`${fieldName}.name`, "");
                  }
                }}
              />
              <FormTextInput
                label="Bank Name"
                name={`${fieldName}.name`}
                required
                disabled
              />
              <FormRadioGroup
                label="Account Type"
                name={`${fieldName}.accountType`}
                options={
                  !!resident && resident !== "1"
                    ? [
                        { label: "NRE", value: "NRE" },
                        { value: "NRO", label: "NRO" },
                      ]
                    : [
                        { label: "Savings", value: "Savings A/C" },
                        { label: "Current", value: "Current A/C" },
                      ]
                }
                required
                fullWidth={true}
                onChange={(e) => {
                  const { value } = e.target;
                  if (!isBackToSummary || isChanged.current) {
                    setFieldValue(`${fieldName}.accountType`, value);
                    return;
                  }
                  setValues({
                    ...values,
                    [fieldName]: {
                      accountType: value,
                      accountNumber: "",
                      accountNumber2: "",
                      termNDCond: "",
                      name: "",
                      ifsc: "",
                    },
                  });
                  isChanged.current = true;
                }}
              />
              <FormTextInput
                label="Account Number (Masked)"
                name={`${fieldName}.accountNumber`}
                type="password"
                required
              />
              <FormTextInput
                label="Confirm Account Number"
                name={`${fieldName}.accountNumber2`}
                type="password"
                required
              />
              <CheckboxField
                label={
                  <>
                    I Accept to the &nbsp;
                    <a
                      onClick={(e) => {
                        e.stopPropagation();
                        openModal();
                        console.log("yes");
                      }}
                    >
                      <u>Terms and Conditions</u>
                    </a>
                  </>
                }
                name={`${fieldName}.termNDCond`}
              />
            </div>
          </div>
        );
      }}
    </Field>
  );
};

const BankDetails = (props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    ackNumber: AckNumber,
    accountType: AT,
    bankDetails: BD,
    dispatch: dispatchContext,
    personalDetails,
    isBackToSummary,
  } = useNewSubscriberContext();
  const resident = personalDetails?.data?.ResidenceType;
  const T2BankSameAsT1Bank = BD?.data?.T2BankSameAsT1Bank;
  const hasTierTwo = AT?.data?.AccountType === "2";
  const initialValues = {
    tier1: {
      ifsc: BD?.data?.BankIFCS || "",
      name: BD?.data?.BankName || "",
      accountType: BD?.data?.BankAccountTypeDes || "Savings A/C",
      accountNumber: BD?.data?.BankAccountNumber || "",
      accountNumber2: BD?.data?.BankAccountNumber || "",
      termNDCond: true,
    },
    ...(hasTierTwo
      ? {
          isBankSameAsTier1: !!T2BankSameAsT1Bank
            ? T2BankSameAsT1Bank === "Y"
            : true,
          tier2: {
            ifsc: BD?.data?.T2BankIFSC || "",
            name: BD?.data?.T2BankName || "",
            accountType:
              BD?.data?.T2SelectedAccountType === "54"
                ? "Current A/C"
                : "Savings A/C",
            accountNumber: BD?.data?.T2BankAccountNumber || "",
            accountNumber2: BD?.data?.T2BankAccountNumber || "",
            termNDCond: true,
          },
        }
      : {}),
  };

  const [open, setOpen] = useState(false);

  const openModal = () => {
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
  };

  const commonValidationSchema = Yup.object().shape({
    ifsc: Yup.string()
      .required("IFSC Number is required")
      .matches(/^[A-Za-z]{4}[A-Z0-9]{7}$/, "Enter a valid IFSC Number")
      .test("ifsc", "IFSC Number cannot be validated", async (value) => {
        const result = /^[A-Za-z]{4}[0-9]{7}$/.test(value);
        if (!result) {
          return true;
        }
        const response = await fetchBankNameFromIFSC({ ifsc: value, dispatch });
        return +response.statusCode === 10000;
      }),
    name: Yup.string()
      .required("Name is required")
      .matches(/^[a-zA-Z ]+$/, "Enter a valid Name"),
    accountType: Yup.string()
      // .oneOf(
      //   ["Savings A/C", "Current A/C", "NRE", "NRO"],
      //   "Account Type is required"
      // )
      .required("Account Type is required"),
    accountNumber: Yup.string()
      .required("Account Number is required")
      .matches(/^[0-9]{9,18}$/, "Enter a valid Account Number"),
    accountNumber2: Yup.string()
      .required("Confirm Account Number is required")
      .oneOf(
        [Yup.ref("accountNumber")],
        "Confirm account number should match account number"
      ),
    termNDCond: Yup.boolean().oneOf(
      [true],
      "Accept Terms and Conditions to proceed further"
    ),
  });

  const validationSchema = Yup.object().shape({
    tier1: commonValidationSchema,
    ...(hasTierTwo
      ? {
          isBankSameAsTier1: Yup.boolean(),
          tier2: commonValidationSchema.when("isBankSameAsTier1", {
            is: true,
            then: (schema) => Yup.mixed().notRequired(),
            otherwise: (schema) => schema,
          }),
        }
      : {}),
  });

  const _handleSubmit = async (values) => {
    try {
      dispatch(showLoader());

      const params = {
        AckNumber,
        AccountType: hasTierTwo ? "2" : "1",
        BankAccountTypeDes: values.tier1.accountType,
        BankAccountType: values.tier1.accountType === "Savings A/C" ? 53 : 54,
        BankAccountNumber: values.tier1.accountNumber,
        ConfirmBankAccountNumber: values.tier1.accountNumber2,
        BankIFCS: values.tier1.ifsc,
        BankName: values.tier1.name,
        ...(hasTierTwo
          ? {
              T2BankSameAsT1Bank: values.isBankSameAsTier1 ? "Y" : "N",
              T2BankAccountType: values.isBankSameAsTier1
                ? values.tier1.accountType === "Savings A/C"
                  ? 53
                  : 54
                : values.tier2.accountType === "Savings A/C"
                ? 53
                : 54,
              T2BankAccountNumber: values.isBankSameAsTier1
                ? values.tier1.accountNumber
                : values.tier2.accountNumber,
              T2ConfirmBankAccountNumber: values.isBankSameAsTier1
                ? values.tier1.accountNumber2
                : values.tier2.accountNumber2,
              T2BankIFSC: values.isBankSameAsTier1
                ? values.tier1.ifsc
                : values.tier2.ifsc,
              T2BankName: values.isBankSameAsTier1
                ? values.tier1.name
                : values.tier2.name,
            }
          : {}),
      };

      const paramsPenny = {
        beneficiaryIFSC: values.tier1.ifsc,
        beneficiaryAccount: values.tier1.accountNumber,
        beneficiaryName: values.tier1.name,
        AckNumber,
        tier: 1,
      };
      const responsePenny = await dispatch(savePennyDetails(paramsPenny));

      if (hasTierTwo) {
        const paramsPenny = {
          beneficiaryIFSC: values.isBankSameAsTier1
            ? values.tier1.ifsc
            : values.tier2.ifsc,
          beneficiaryAccount: values.isBankSameAsTier1
            ? values.tier1.accountNumber
            : values.tier2.accountNumber,
          beneficiaryName: values.isBankSameAsTier1
            ? values.tier1.name
            : values.tier2.name,
          tier: 2,
          AckNumber,
        };
        const responsePenny = await dispatch(savePennyDetails(paramsPenny));
        dispatchContext(
          setBasicDetails({
            tier2PennyDrop: responsePenny?.data?.data
              ? responsePenny.data
              : responsePenny,
          })
        );
      }
      const response = await dispatch(saveBankDetails(params));

      if (Number(response.statusCode) !== SUCCESS_RESPONSE_CODE) {
        throw new Error(response?.message || "Error in saving Bank details");
      }

      const responseBank = await dispatch(getBankDetails({ AckNumber }));

      dispatchContext(
        setPennyDropTypes(
          responsePenny?.data?.data ? responsePenny.data : responsePenny
        )
      );

      dispatchContext(setBankDetailsContext(responseBank));

      if (isBackToSummary) {
        dispatchContext(setStatusBackToSummary(false));
      }

      history.push("/nps/AddSubs/upload-documents");
    } catch (e) {
      console.error(e);
    } finally {
      dispatch(hideLoader());
    }
  };

  useEffect(() => {
    const fetchBankDetails = async () => {
      await dispatch(getBankDetails({ AckNumber }));
    };
    fetchBankDetails();
  }, [dispatch, AckNumber]);

  const goBack = () => {
    history.push({
      pathname: "/nps/AddSubs/nominee-details",
    });
  };
  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={_handleSubmit}
      >
        {({ values, setFieldValue, validateField, errors }) => (
          <Form autoComplete="off">
            <CardComponent title="Tier I">
              <BankSection
                name={"tier1"}
                resident={resident}
                openModal={openModal}
              />
            </CardComponent>
            {hasTierTwo && (
              <div className="container-margin">
                <div className="new-investor-card-header">
                  TIER II
                  <SimpleCheckboxFormikField
                    name={"isBankSameAsTier1"}
                    style={{
                      marginLeft: "30px",
                      cursor: "pointer",
                      color: "#003d7b",
                    }}
                  />
                  Bank Detail(s) same as Tier I
                </div>
                {!values.isBankSameAsTier1 && (
                  <div className="new-investor-card">
                    <BankSection
                      name={"tier2"}
                      resident={resident}
                      openModal={openModal}
                    />
                  </div>
                )}
              </div>
            )}
            <div className="d-flex justify-content-center">
              <button
                className="new-investor-button"
                onClick={goBack}
                disabled={!goBack}
                style={{
                  border: "1px solid #007ac8",
                  color: "#007ac8",
                }}
              >
                Back
              </button>
              <button
                type="submit"
                className="new-investor-button new-investor-active"
                // For disabling the btn if terms&condition not checked
                // disabled={
                //   values?.isBankSameAsTier1
                //     ? !values?.tier1?.termNDCond
                //     : !values?.tier1?.termNDCond || !values?.tier2?.termNDCond
                // }
              >
                Proceed
              </button>
            </div>
          </Form>
        )}
      </Formik>
      <CustomizedDialogs open={open} closeModal={closeModal} />
    </>
  );
};

export default BankDetails;

export const fetchBankDetails = async (
  _inputField,
  value,
  regex,
  setState,
  dispatch
) => {
  var error = "";
  if (!value) {
    error = "IFSC Number is required";
  } else if (!regex.test(value)) {
    error = "Enter a valid IFSC Number";
  }
  if (!error) {
    const response = await dispatch(getBankDetailsFromIFSC({ number: value }));
    if (+response.statusCode === 10000) {
      setState((prevState) => {
        return {
          ...prevState,
          [_inputField]: response.data[0].BankName,
        };
      });
    } else {
      error = "Enter a valid IFSC Number";
    }
  }
  return error;
};

class CustomizedDialogs extends React.Component {
  render() {
    return (
      <div>
        <Dialog
          onClose={this.props.closeModal}
          aria-labelledby="customized-dialog-title"
          open={this.props.open}
          fullWidth={true}
        >
          <DialogTitle
            // id="customized-dialog-title"
            onClose={this.props.closeModal}
          >
            Terms And Conditions
          </DialogTitle>
          <DialogContent>
            <div>
              <div className="modal-text2">
                By clicking on proceed button your bank details will be verified
                through Penny drop process which is a chargeable transaction,
                Charges for Bank verification is Rs. 1.90 plus applicable taxes.
                <br /> If your updated bank details for Tier I and Tier II are
                different. your bank details would be verified tier wise and
                charged accordingly and if Bank details are same in both the
                Tiers it would verified once and charged accordingly.
                <br /> If Bank verification is successful, you have to
                authenticate the same through Authentication through OTPs or
                eSign through Aadhaar.
                <br /> If bank verification fails, subscriber is requested to
                submit the physical Subscriber Modification Form to associated
                Nodal Office for updation of bank details.
              </div>
            </div>

            <div className="modal-continue-button">
              <div style={{ display: "flex", justifyContent: "center" }}>
                <button
                  type="submit"
                  className="new-investor-button new-investor-active"
                  onClick={this.props.closeModal}
                >
                  Ok
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }
}
